import { useTranslation } from "react-i18next";

export default function BenefitsSection() {
  const { t } = useTranslation();

  const advantages = [
    { icon: "ri-shield-check-line", titleKey: "benefits_adv1_title", descKey: "benefits_adv1_desc" },
    { icon: "ri-line-chart-line", titleKey: "benefits_adv2_title", descKey: "benefits_adv2_desc" },
    { icon: "ri-money-euro-circle-line", titleKey: "benefits_adv3_title", descKey: "benefits_adv3_desc" },
    { icon: "ri-global-line", titleKey: "benefits_adv4_title", descKey: "benefits_adv4_desc" },
    { icon: "ri-user-heart-line", titleKey: "benefits_adv5_title", descKey: "benefits_adv5_desc" },
    { icon: "ri-time-line", titleKey: "benefits_adv6_title", descKey: "benefits_adv6_desc" },
  ];

  const testimonials = [
    {
      textKey: "benefits_t1_text", nameKey: "benefits_t1_name", metaKey: "benefits_t1_meta",
      img: "https://public.readdy.ai/ai/img_res/48ade2bb2d18962ed5f5a43664b5ba34.jpg",
    },
    {
      textKey: "benefits_t2_text", nameKey: "benefits_t2_name", metaKey: "benefits_t2_meta",
      img: "https://public.readdy.ai/ai/img_res/52f08d5cd4a41a0b8cde22f6c8941852.jpg",
    },
    {
      textKey: "benefits_t3_text", nameKey: "benefits_t3_name", metaKey: "benefits_t3_meta",
      img: "https://readdy.ai/api/search-image?query=happy%20elderly%20danish%20woman%20smiling%20portrait%20professional%20photo%20warm%20tones%20natural%20light%20retirement%20age%2064%20years%20old%20confident%20look&width=80&height=80&seq=t3&orientation=squarish",
    },
  ];

  const stats = [
    { icon: "ri-money-euro-circle-line", value: "€19,800", labelKey: "benefits_stat1", bg: "bg-emerald-700 text-white", iconBg: "bg-white/20" },
    { icon: "ri-line-chart-line", value: "198%", labelKey: "benefits_stat2", bg: "bg-amber-400 text-gray-900", iconBg: "bg-white/20" },
    { icon: "ri-calendar-check-line", value: "12×", labelKey: "benefits_stat3", bg: "bg-stone-100 text-gray-900", iconBg: "bg-white/20" },
    { icon: "ri-shield-check-line", value: "100%", labelKey: "benefits_stat4", bg: "bg-stone-100 text-gray-900", iconBg: "bg-white/20" },
  ];

  const listItems = ["benefits_li1", "benefits_li2", "benefits_li3", "benefits_li4"];

  return (
    <section id="benefits" className="py-14 md:py-24 bg-stone-50">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-10 md:mb-14">
          <div className="inline-flex items-center gap-2 bg-emerald-50 text-emerald-700 text-xs font-semibold px-4 py-2 rounded-full mb-4">
            <i className="ri-star-line"></i>
            {t("benefits_badge")}
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3">{t("benefits_title")}</h2>
          <p className="text-gray-500 text-sm md:text-base max-w-xl mx-auto">{t("benefits_desc")}</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 mb-12 md:mb-20">
          {advantages.map((item) => (
            <div key={item.titleKey} className="bg-white rounded-2xl p-5 md:p-7 border border-gray-100 hover:-translate-y-1 transition-transform">
              <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-xl bg-emerald-50 text-emerald-700 mb-4">
                <i className={`${item.icon} text-lg md:text-xl`}></i>
              </div>
              <h3 className="text-sm md:text-base font-bold text-gray-900 mb-2">{t(item.titleKey)}</h3>
              <p className="text-xs md:text-sm text-gray-500 leading-relaxed">{t(item.descKey)}</p>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-10 items-center mb-12 md:mb-20">
          <div>
            <div className="inline-flex items-center gap-2 bg-emerald-50 text-emerald-700 text-xs font-semibold px-4 py-2 rounded-full mb-5">
              <i className="ri-money-euro-circle-line"></i>
              {t("benefits_passive_badge")}
            </div>
            <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4 leading-tight">
              {t("benefits_passive_title1")} <span className="text-emerald-700">{t("benefits_passive_title2")}</span>
            </h3>
            <p className="text-gray-600 text-sm md:text-base leading-relaxed mb-4">{t("benefits_passive_desc1")}</p>
            <p className="text-gray-600 text-sm md:text-base leading-relaxed mb-6">{t("benefits_passive_desc2")}</p>
            <ul className="flex flex-col gap-2.5 mb-6">
              {listItems.map((key) => (
                <li key={key} className="flex items-center gap-3">
                  <div className="w-5 h-5 flex items-center justify-center text-emerald-600 flex-shrink-0">
                    <i className="ri-check-line font-bold"></i>
                  </div>
                  <span className="text-gray-700 text-sm">{t(key)}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="grid grid-cols-2 gap-3 md:gap-4">
            {stats.map((stat) => (
              <div key={stat.value} className={`rounded-2xl p-4 md:p-6 flex flex-col gap-2 md:gap-3 ${stat.bg}`}>
                <div className={`w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-xl ${stat.iconBg}`}>
                  <i className={`${stat.icon} text-base md:text-xl`}></i>
                </div>
                <div className="text-2xl md:text-3xl font-bold">{stat.value}</div>
                <div className="text-xs md:text-sm opacity-70">{t(stat.labelKey)}</div>
              </div>
            ))}
          </div>
        </div>

        <div>
          <div className="text-center mb-8">
            <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">{t("benefits_reviews_title")}</h3>
            <p className="text-gray-500 text-sm">{t("benefits_reviews_sub")}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
            {testimonials.map((item) => (
              <div key={item.nameKey} className="bg-white rounded-2xl p-5 md:p-7 border border-gray-100">
                <div className="flex items-center gap-1 text-amber-400 mb-3">
                  {[...Array(5)].map((_, i) => <i key={i} className="ri-star-fill text-sm"></i>)}
                </div>
                <p className="text-gray-600 text-sm leading-relaxed mb-5 italic">{t(item.textKey)}</p>
                <div className="flex items-center gap-3">
                  <img alt={t(item.nameKey)} className="w-10 h-10 md:w-11 md:h-11 rounded-full object-cover object-top flex-shrink-0" src={item.img} />
                  <div>
                    <div className="text-sm font-bold text-gray-900">{t(item.nameKey)}</div>
                    <div className="text-xs text-gray-400">{t(item.metaKey)}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
